import { inject, Injectable } from '@angular/core'
import { PrivateMessagesService } from '../domain/privateMessages/private-messages.service'
import { AnnouncementsService } from '../domain/announcements/announcements.service'
import { Announcement } from '../../models/announcement'
import { Message } from '../../features/messaging/message'
import { PrivateMessage } from '../../models/privateMessage'
import { combineLatest, Observable, of } from 'rxjs'
import { first, map, shareReplay, switchMap } from 'rxjs/operators'
import { SmsMessage } from '../../models/smsMessage'
import { SmsService } from '../domain/sms/sms.service'
import { LazyModalService } from '../../features/LazyModal/LazyModalService'
import { ModalController } from '@ionic/angular'
import { MessagingPage } from '../../pages/messaging/messaging/messaging-page.component'
import { UsersService } from '../domain/users.service'
import { MessageModes, MessageType } from '../../pages/messaging/messaging/messageModes'

@Injectable({
  providedIn: 'root',
})
export class MessagesService extends LazyModalService<void> {
  private privateMessagesService = inject(PrivateMessagesService)
  private announcementsService = inject(AnnouncementsService)
  private smsService = inject(SmsService)
  private userService = inject(UsersService)
  protected modalCtrl: ModalController

  private TAG = 'MessagesService'

  constructor() {
    const modalCtrl = inject(ModalController)

    super(modalCtrl)

    this.modalCtrl = modalCtrl
  }

  async open() {
    console.log(`${this.TAG}#open`)
    return await this.modalCtrl.create({
      component: MessagingPage,
      cssClass: 'messaging',
      componentProps: {},
    })
  }

  getAnnouncementsForCurrentUser(): Observable<Announcement[]> {
    return this.announcementsService.getAnnouncementsForCurrentUser()
  }

  getCombinedUnreadMessagesCount() {
    return this.userService.getCurrentUserWorkgroup().pipe(
      first(),
      switchMap((workgroup) =>
        this.userService.getCurrentUser().pipe(
          first(),
          switchMap((user) => {
            const smsCount$ = user.isInRoleGroup(MessageModes.getByType(MessageType.sms).allowedRoles)
              ? this.smsService.getUnreadCountForCurrentUser(workgroup)
              : of(0)

            return combineLatest([
              this.privateMessagesService.getUnreadCountForCurrentUser(),
              this.announcementsService.getUnreadCountForCurrentUser(),
              smsCount$,
            ])
          }),
        ),
      ),
      map((counts) => counts.reduce((a, b) => a + b, 0)),
      shareReplay({ bufferSize: 1, refCount: true }),
    )
  }

  async send(message: Message) {
    console.log(this.TAG, 'send', message)
    if (message instanceof Announcement) await this.announcementsService.send(message)
    if (message instanceof PrivateMessage) await this.privateMessagesService.send(message)
    if (message instanceof SmsMessage) await this.smsService.send(message)
  }
}
